const SNAP_ALIGNMENT_TOLERANCE = 1;

/**
 * Cabinet swap changes the feature/layout in-place, so the shape can move from
 * a rectangle to an asymmetric contour like an L-corner. Nominal width/depth is
 * not enough to keep those shapes aligned because shape position is not always
 * the same as the visible bounding-box edge.
 *
 * This helper captures the snapped edge relationship before swap, lets the
 * existing resize auto-shift do its group work, then restores only the alignment
 * that existed before the feature changed:
 * - snapped neighbours keep their cross-axis row/column position;
 * - the swapped shape is moved by actual contour bounds so its shared edge stays flush.
 */

function getShapeBoundsOnWorkspace(shape) {
  const points = shape.getPointsPosOnWorkspace ? shape.getPointsPosOnWorkspace() : [];

  if (shape.getBumpoutPointsPosOnWorkspace) {
    points.push(...shape.getBumpoutPointsPosOnWorkspace());
  }

  if (!points.length) {
    return null;
  }

  return {
    minX: Math.min(...points.map(point => point.x)),
    maxX: Math.max(...points.map(point => point.x)),
    minY: Math.min(...points.map(point => point.y)),
    maxY: Math.max(...points.map(point => point.y)),
  };
}

function rangesOverlap(firstStart, firstEnd, secondStart, secondEnd) {
  return Math.min(firstEnd, secondEnd) - Math.max(firstStart, secondStart) > SNAP_ALIGNMENT_TOLERANCE;
}

function getBestAlignment(currentAlignment, nextAlignment) {
  if (!nextAlignment) {
    return currentAlignment;
  }

  if (!currentAlignment || nextAlignment.overlap > currentAlignment.overlap) {
    return nextAlignment;
  }

  return currentAlignment;
}

function getSharedAxisAlignment(shapeMin, shapeMax, snappedMin, snappedMax, snappedShape, overlap) {
  if (Math.abs(shapeMin - snappedMin) <= SNAP_ALIGNMENT_TOLERANCE) {
    return { edge: 'min', snappedShape, overlap };
  }

  if (Math.abs(shapeMax - snappedMax) <= SNAP_ALIGNMENT_TOLERANCE) {
    return { edge: 'max', snappedShape, overlap };
  }

  return null;
}

function getSwapEdgeAlignment(shape, snappedShapes) {
  const shapeBounds = getShapeBoundsOnWorkspace(shape);
  if (!shapeBounds) {
    return {};
  }

  return snappedShapes.reduce((alignment, snappedShape) => {
    const snappedBounds = getShapeBoundsOnWorkspace(snappedShape);
    if (!snappedBounds) {
      return alignment;
    }

    const verticalOverlap = Math.min(shapeBounds.maxY, snappedBounds.maxY) - Math.max(shapeBounds.minY, snappedBounds.minY);
    const horizontalOverlap = Math.min(shapeBounds.maxX, snappedBounds.maxX) - Math.max(shapeBounds.minX, snappedBounds.minX);
    const isTouchingHorizontally = rangesOverlap(shapeBounds.minY, shapeBounds.maxY, snappedBounds.minY, snappedBounds.maxY)
      && (
        Math.abs(shapeBounds.maxX - snappedBounds.minX) <= SNAP_ALIGNMENT_TOLERANCE
        || Math.abs(shapeBounds.minX - snappedBounds.maxX) <= SNAP_ALIGNMENT_TOLERANCE
      );
    const isTouchingVertically = rangesOverlap(shapeBounds.minX, shapeBounds.maxX, snappedBounds.minX, snappedBounds.maxX)
      && (
        Math.abs(shapeBounds.maxY - snappedBounds.minY) <= SNAP_ALIGNMENT_TOLERANCE
        || Math.abs(shapeBounds.minY - snappedBounds.maxY) <= SNAP_ALIGNMENT_TOLERANCE
      );

    if (isTouchingHorizontally) {
      alignment.y = getBestAlignment(alignment.y, getSharedAxisAlignment(
        shapeBounds.minY,
        shapeBounds.maxY,
        snappedBounds.minY,
        snappedBounds.maxY,
        snappedShape,
        verticalOverlap,
      ));
    }

    if (isTouchingVertically) {
      alignment.x = getBestAlignment(alignment.x, getSharedAxisAlignment(
        shapeBounds.minX,
        shapeBounds.maxX,
        snappedBounds.minX,
        snappedBounds.maxX,
        snappedShape,
        horizontalOverlap,
      ));
    }

    return alignment;
  }, {});
}

function getShapePositionSnapshot(shapes) {
  return shapes.reduce((positionsById, currentShape) => {
    positionsById[currentShape.id] = currentShape.getPosition();
    return positionsById;
  }, {});
}

function setShapePosition(shape, position) {
  shape.setPosition(position);
  if (shape.shapeAbsolute) {
    shape.shapeAbsolute.setAbsolutePos(position);
  }
}

function moveShapeByOffset(shape, offset) {
  if (offset.x === 0 && offset.y === 0) {
    return;
  }

  const currentPosition = shape.getPosition();
  setShapePosition(shape, {
    x: currentPosition.x + offset.x,
    y: currentPosition.y + offset.y,
  });
}

function restoreSnappedShapesCrossAxis(snappedShapes, positionsById, alignment) {
  snappedShapes.forEach((snappedShape) => {
    const originalPosition = positionsById[snappedShape.id];
    if (!originalPosition) {
      return;
    }

    const currentPosition = snappedShape.getPosition();
    const restoredPosition = {...currentPosition};

    if (alignment.y) {
      restoredPosition.y = originalPosition.y;
    }

    if (alignment.x) {
      restoredPosition.x = originalPosition.x;
    }

    setShapePosition(snappedShape, restoredPosition);
  });
}

function applySwapEdgeAlignment(shape, alignment) {
  const shapeBounds = getShapeBoundsOnWorkspace(shape);
  if (!shapeBounds) {
    return;
  }

  const offset = { x: 0, y: 0 };

  if (alignment.x) {
    const snappedBounds = getShapeBoundsOnWorkspace(alignment.x.snappedShape);
    if (snappedBounds) {
      offset.x = alignment.x.edge === 'min'
        ? snappedBounds.minX - shapeBounds.minX
        : snappedBounds.maxX - shapeBounds.maxX;
    }
  }

  if (alignment.y) {
    const snappedBounds = getShapeBoundsOnWorkspace(alignment.y.snappedShape);
    if (snappedBounds) {
      offset.y = alignment.y.edge === 'min'
        ? snappedBounds.minY - shapeBounds.minY
        : snappedBounds.maxY - shapeBounds.maxY;
    }
  }

  moveShapeByOffset(shape, offset);
}

export function createSwapCabinetAlignmentSnapshot(shape, snappedShapes) {
  // Must run before changeShapeLayout(), while the old contour still shows
  // which edge and row/column were shared with snapped neighbours.
  return {
    snappedShapePositions: getShapePositionSnapshot(snappedShapes),
    edgeAlignment: getSwapEdgeAlignment(shape, snappedShapes),
  };
}

export function restoreSwapCabinetAlignment(shape, snappedShapes, alignmentSnapshot) {
  if (!alignmentSnapshot) {
    return;
  }

  // Auto-shift may move neighbours on the cross axis when an L-corner changes
  // depth/width. Restore that row/column, then align the new contour to the same edge.
  restoreSnappedShapesCrossAxis(
    snappedShapes,
    alignmentSnapshot.snappedShapePositions,
    alignmentSnapshot.edgeAlignment,
  );
  applySwapEdgeAlignment(shape, alignmentSnapshot.edgeAlignment);
}
